import React from 'react';

// =============================================================================
// PerDiemPanel
// =============================================================================
// The Accounting workspace's Per Diem grid (legacy dash_CSR_PerDiem). Two value
// columns per row — the PRO count and the total per-diem days — fed by
// /lc-dashboards/per-diem-counts. Rows are clickable: a click drills the bucket's
// PROs into the standard results area (onSelect(key)). Not Approved and Pending
// Final App turn red when their count is > 0 (legacy doColorClass '>0'); the rest
// are neutral. Purely presentational — the page owns data fetching + drill wiring.

export interface PerDiemData {
  counts: { [key: string]: number };
  days: { [key: string]: number };
}

export interface PerDiemPanelProps {
  data: PerDiemData | null;
  loading: boolean;
  onSelect: (key: string) => void;
}

interface PDRow {
  key: string;
  label: string;
  indent?: number;     // nesting depth (0 = flush)
  bold?: boolean;      // emphasize sub-totals (Approved)
  redWhenPositive?: boolean; // flag the count chip red when > 0
}

// Order + hierarchy match the legacy table exactly.
const ROWS: PDRow[] = [
  { key: 'lc-pd-not-approved', label: 'Not Approved', redWhenPositive: true },
  { key: 'lc-pd-not-approved-terminated', label: 'Terminated', indent: 1 },
  { key: 'lc-pd-approved', label: 'Approved', bold: true },
  { key: 'lc-pd-gulf-winds', label: 'Gulf Winds', indent: 1 },
  { key: 'lc-pd-customer', label: 'Customer', indent: 1 },
  { key: 'lc-pd-gwi-customer', label: 'GWI/Customer', indent: 1 },
  { key: 'lc-pd-dispute', label: 'Dispute', indent: 1 },
  { key: 'lc-pd-ssline-waived', label: 'SSLine Waived', indent: 1 },
  { key: 'lc-pd-pending-final-app', label: 'Pending Final App', indent: 1, redWhenPositive: true },
];

const PerDiemPanel: React.FC<PerDiemPanelProps> = ({ data, loading, onSelect }) => {
  const fmt = (n: number | undefined) => (n === undefined ? (loading ? '…' : '0') : n.toLocaleString('en-US'));

  return (
    // w-full so the table fills the tile width (header + columns stretch to the panel edges);
    // natural height keeps the tile's double-click fit-to-content working.
    <div className="w-full bg-panel text-sm">
      <table className="border-collapse w-full">
        <thead>
          <tr className="bg-slate-700 text-white">
            <th className="px-2 py-1 font-semibold text-left min-w-[150px]">Per Diem</th>
            <th className="px-2 py-1 font-semibold text-right border-l border-slate-500 min-w-[64px]">Pros</th>
            <th className="px-2 py-1 font-semibold text-right border-l border-slate-500 min-w-[64px]">Days</th>
          </tr>
        </thead>
        <tbody className="text-body">
          {ROWS.map((row) => {
            const count = data?.counts?.[row.key];
            const days = data?.days?.[row.key];
            const red = row.redWhenPositive && typeof count === 'number' && count > 0;
            const chip = red
              ? 'bg-red-600 text-white rounded px-1.5'
              : '';
            return (
              <tr
                key={row.key}
                onClick={() => onSelect(row.key)}
                title={`View ${row.label}`}
                className="cursor-pointer border-t border-gray-100 dark:border-gray-700/50 hover:bg-blue-50 dark:hover:bg-slate-700/40 transition-colors"
              >
                <td
                  className={`px-2 py-1 whitespace-nowrap ${row.bold ? 'font-semibold' : ''}`}
                  style={{ paddingLeft: `${8 + (row.indent || 0) * 14}px` }}
                >
                  {row.label}
                </td>
                <td className="px-2 py-1 text-right tabular-nums border-l border-gray-100 dark:border-gray-700/50">
                  <span className={`font-semibold ${chip}`}>{fmt(count)}</span>
                </td>
                <td className="px-2 py-1 text-right tabular-nums border-l border-gray-100 dark:border-gray-700/50">
                  <span className={`font-semibold ${chip}`}>{fmt(days)}</span>
                </td>
              </tr>
            );
          })}
          {!data && loading && (
            <tr><td colSpan={3} className="px-2 py-4 text-center text-gray-400">Loading…</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PerDiemPanel;
